import { ChangeEvent, FocusEvent, FunctionComponent, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/pro-solid-svg-icons';
import stock from '../StockSidebar/StockSidebar.module.scss';

const MAX_QUANTITY = 99999;

interface QuantityStepperProps {
    quantity: number;
    disabled?: boolean;
    min?: number;
    max?: number;
    onChange: (quantity: number) => void;
}

export const QuantityStepper: FunctionComponent<QuantityStepperProps> = ({
    quantity,
    disabled,
    min = 1,
    max = MAX_QUANTITY,
    onChange
}) => {
    const [text, setText] = useState(String(quantity));
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) {
            setText(String(quantity));
        }
    }, [quantity, focused]);

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const digits = event.target.value.replace(/\D/g, '').slice(0, 5);

        if (digits === '') {
            setText('');
            return;
        }

        const parsed = Number(digits);

        if (parsed < min || parsed > max) {
            return;
        }

        setText(String(parsed));
        onChange(parsed);
    };

    const handleBlur = () => {
        setFocused(false);

        if (text === '' || Number(text) < min) {
            setText(String(min));
            onChange(min);
        }
    };

    const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
        setFocused(true);
        event.target.select();
    };

    return (
        <div className={stock.stepper}>
            <button
                type="button"
                className={stock.stepperButton}
                onClick={() => onChange(Math.max(min, quantity - 1))}
                disabled={disabled || quantity <= min}
                aria-label="Decrease quantity"
            >
                <FontAwesomeIcon icon={faMinus} />
            </button>
            <input
                className={stock.stepperValue}
                value={text}
                inputMode="numeric"
                maxLength={5}
                disabled={disabled}
                aria-label="Quantity"
                onFocus={handleFocus}
                onBlur={handleBlur}
                onChange={handleChange}
            />
            <button
                type="button"
                className={stock.stepperButton}
                onClick={() => onChange(Math.min(max, quantity + 1))}
                disabled={disabled || quantity >= max}
                aria-label="Increase quantity"
            >
                <FontAwesomeIcon icon={faPlus} />
            </button>
        </div>
    );
};

export default QuantityStepper;
